import { useAuth } from "@healthos/auth/react";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { branchDeepLinkPath } from "../lib/branch";

/**
 * Routes a visit that arrived through a Branch link to the surface that link
 * asked for.
 *
 * This is the web half of the deep-link contract the mobile app already
 * honours: a Branch link carries a `$deeplink_path`, and whichever client
 * opens it is expected to land the member there rather than on the default
 * home surface. A member who taps "read your brief" in a shared link and gets
 * dropped on `/today` has been told the link is broken.
 *
 * Three things make this a hook rather than a line in `boot()`:
 *
 *  - It needs the router, which is not mounted during boot.
 *  - It must wait for sign-in. Navigating while the OIDC redirect is in flight
 *    either loses the destination or lands an unauthenticated member on a
 *    surface that immediately bounces them.
 *  - `initBranch` resolves asynchronously and may resolve after first paint,
 *    so the destination has to be picked up rather than read once.
 *
 * At most one navigation per page load, and `replace` rather than `push`, so
 * Back returns to wherever the member came from instead of bouncing them
 * through the deep-link hop. A visit with no Branch link (the overwhelming
 * majority, and every visit when `VITE_BRANCH_KEY` is unset) resolves null and
 * does nothing.
 */
export function useBranchDeepLink(): void {
  const { state } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navigated = useRef(false);

  useEffect(() => {
    if (navigated.current) return;
    if (state.status !== "signedIn") return;
    /*
     * The auth routes unmount themselves the moment the session settles, and
     * navigating out from under them races that. Skipping is only safe because
     * `pathname` is an effect dependency: the session turning signedIn while
     * /callback is still mounted is the common ordering, so this branch is hit
     * on most attributed sign-ins, and without the dependency the effect would
     * never run again and the destination would be dropped for good.
     */
    if (pathname === "/callback" || pathname === "/silent-callback") return;

    let cancelled = false;
    void branchDeepLinkPath().then((path) => {
      if (cancelled || navigated.current || !path) return;
      // Marked handled either way, so a later route change cannot navigate on
      // a destination this load has already honoured.
      navigated.current = true;
      if (path !== pathname) void navigate(path, { replace: true });
    });

    return () => {
      cancelled = true;
    };
  }, [state, navigate, pathname]);
}
